import React, { Suspense } from 'react';
import { usePromise } from '../../hooks';
import { AuthService } from '../../services';
import { IModel } from '../../types';
import { embedPwWebComponent, embedShareComponent } from '../../utils';
import { LoadingPage, Online } from '../Shared';
import { AppDeprecationNotice } from './AppDeprecationNotice';
import { ConnectedHeader } from './Header/ConnectedHeader';
import { ServiceAuth } from './ServiceAuth';
import { FRAME_ID, TeamsFrame } from './TeamsFrame';

const PwWebPlugin = React.lazy(async () =>
  import('./PwWebPlugin').then(module => ({
    default: module.PwWebPlugin,
  }))
);
const DesignReview = React.lazy(async () =>
  import('./DesignReview').then(module => ({
    default: module.DesignReview,
  }))
);

const getFrameStyles = (frameId: string, headerHeight: number) => {
  return {
    __html: `
      #${frameId} iframe {
        min-height: calc(100vh - ${headerHeight}px);
        border: 0;
        width: 100%;
        height: 100%;
      }

      main.fit-to-parent {
        background-color: white;
      }
    `,
  };
};

export interface TeamsFrameContainerLayoutProps {
  authExternalCallback?: (e?: any) => void;
  connectionName?: string;
  frameUrl?: string;
  linkToUrl?: string;
  projectId: string;
  serviceName?: string;
  subEntityId?: string;
  iModel?: IModel;
}

export function TeamsFrameContainerLayout({
  authExternalCallback,
  connectionName,
  frameUrl,
  linkToUrl,
  projectId,
  serviceName,
  subEntityId,
  iModel,
}: TeamsFrameContainerLayoutProps): JSX.Element {
  const user = usePromise(AuthService.getUser());
  const renderHeader =
    !authExternalCallback && serviceName !== 'ProjectWise 365';

  return (
    <Online>
      <style
        type='text/css'
        dangerouslySetInnerHTML={getFrameStyles(
          FRAME_ID,
          renderHeader ? 31 : 0
        )}
      />
      {user && renderHeader && (
        <ConnectedHeader user={user} linkToUrl={linkToUrl} />
      )}
      <div id='embedded-content' style={{ height: 'calc(100% - 42px)' }}>
        <Suspense
          fallback={
            <LoadingPage subtext='Adding the final touches...' fullscreen />
          }
        >
          {embedShareComponent(serviceName, connectionName) ? (
            <AppDeprecationNotice linkToUrl={linkToUrl} />
          ) : embedPwWebComponent(serviceName) ? (
            user && (
              <PwWebPlugin
                user={user}
                linkToUrl={linkToUrl}
                projectId={projectId}
                subEntityId={subEntityId}
              />
            )
          ) : serviceName === 'Design Review' ? (
            <DesignReview
              projectId={projectId}
              iModelId={iModel?.iModelId}
              namedVersionId={iModel?.namedVersionId ?? ''}
              defaultViewId={iModel?.defaultViewId}
            />
          ) : serviceName === 'ProjectWise 365' ? (
            frameUrl && <TeamsFrame frameUrl={frameUrl} />
          ) : (
            <AppDeprecationNotice linkToUrl={frameUrl} />
          )}
        </Suspense>
      </div>
      {authExternalCallback && (
        <ServiceAuth serviceName={serviceName} onLogin={authExternalCallback} />
      )}
    </Online>
  );
}
